import { useState, useCallback, useEffect, useRef } from "react";
import { cloneDeep } from "lodash";
import { Cart, Product, ShippingData, CartProduct } from "@/interfaces";
import { isBoolean } from "@/utils";
import { CartHelper } from "@/helpers";
import { CartContextProps } from "./context";
import { useDealer } from "../dealer";

export const useCartContextController = (): CartContextProps => {
  const [cart, setCart] = useState<Cart | undefined>();
  const { dealer, coopInfo } = useDealer();

  // Always-current ref to the cart so the dealer-change effect below can read the
  // latest cart without listing it as a dependency (which would cause loops).
  const cartRef = useRef<Cart | undefined>(cart);
  cartRef.current = cart;

  const setCartWithCoop = useCallback(
    async (newCart?: Cart) => {
      if (newCart) {
        const recalculated = await CartHelper.recalculateCoop(
          newCart,
          coopInfo?.available,
          coopInfo?.acbAllowanceEnabled,
          coopInfo?.masterDealerId
        );
        setCart(recalculated);
      } else {
        setCart(undefined);
      }
    },
    [setCart, coopInfo]
  );

  // When the dealer loads (or changes), re-run recalculateCoop so the cart picks
  // up real per-mediaId ACB rates. Without this, a cart loaded BEFORE the dealer
  // is selected stays stuck at the fail-closed $0 fallback even after the dealer
  // info arrives — a classic UX bug because nothing else triggers a recalc.
  useEffect(() => {
    if (cartRef.current && coopInfo?.masterDealerId) {
      setCartWithCoop(cartRef.current);
    }
    // Intentionally only depend on masterDealerId / available — re-running when
    // the cart itself changes is already handled by setCartWithCoop in mutation
    // callbacks (add, remove, setQuantity, etc.).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coopInfo?.masterDealerId, coopInfo?.available]);

  const set = useCallback(
    (propertyName?:keyof Cart, value?: Cart[keyof Cart]) => { 
      console.log("set - Property Name: ", propertyName, "Value: ", value);
      

      setCart(prev=>{
        if (!prev || !propertyName) return prev;
        const tmpCart = { ...prev };
        if (!cart || !propertyName) {
          return prev;
        }
        (tmpCart as any)[propertyName] = value;
        console.log("tmpCart",tmpCart)
        return tmpCart;
      });
    },
    [cart, setCart]
  );

  const add = useCallback(
    async (
      item?: Product,
      shippingFee?: number,
      claimFee?: number,
      coopEligible?: number,
      couponAmount?: number,
      isOffSite?:boolean
    ) => {
      if (!item) {
        return;
      }

      const tmpCart = { ...cart } as Cart;

      if (!isNaN(claimFee as number) && isNaN(tmpCart.claimFee as number)) {
        tmpCart.claimFee = claimFee;
      }

      if (
        !isNaN(coopEligible as number) &&
        isNaN(tmpCart.coopEligible as number)
      ) {
        tmpCart.coopEligible = coopEligible;
      }

      if (
        !isNaN(dealer?.dealerId as number) &&
        isNaN(tmpCart.dealerId as number)
      ) {
        tmpCart.dealerId = dealer?.dealerId;
      }

      if (!!coopInfo?.masterDealerId && !tmpCart.masterDealerId) {
        tmpCart.masterDealerId = coopInfo.masterDealerId;
      }

      if (
        !isNaN(couponAmount as number) &&
        isNaN(tmpCart.couponAmount as number)
      ) {
        tmpCart.couponAmount = couponAmount;
      }

      if (isNaN(tmpCart.couponQuantity as number)) {
        tmpCart.couponQuantity = 0;
      }

      if (isNaN(tmpCart.shippingFee as number)) {
        tmpCart.shippingFee = shippingFee;
      }

      if (!Array.isArray(tmpCart.products)) {
        tmpCart.products = [];
      }

      const cartItem = tmpCart.products.find(
        (i) => i.productId === item.productId
      );

      if (cartItem && !isOffSite) {
        ++cartItem.quantity;
      } else {
        let quantityProperty = {quantity:1}
        const tmpCartItem = {
          ...item,
          ...quantityProperty
        } as CartProduct;

        tmpCartItem.coop = tmpCartItem.coopApplied =
          (coopInfo?.available as number) > 0
            ? CartHelper.getItemSubtotal(tmpCartItem)
            : 0;

        tmpCart.products.push(tmpCartItem);
      }

      const recalculated = await CartHelper.recalculateCoop(
        tmpCart,
        coopInfo?.available,
        coopInfo?.acbAllowanceEnabled,
        coopInfo?.masterDealerId
      );
      setCart(recalculated);
    },
    [cart, setCart, dealer, coopInfo]
  );

  const remove = useCallback(
    async (id?: number) => {
      if (isNaN(id as number) || !cart || !Array.isArray(cart.products)) {
        return;
      }

      const tmpCart = { ...cart };

      const cartItemIndex = tmpCart.products.findIndex(
        (i) => i.productId === id
      );

      if (cartItemIndex === -1) {
        return;
      }

      tmpCart.products.splice(cartItemIndex, 1);

      const recalculated = await CartHelper.recalculateCoop(
        tmpCart,
        coopInfo?.available,
        coopInfo?.acbAllowanceEnabled,
        coopInfo?.masterDealerId
      );
      setCart(recalculated);
    },
    [cart, setCart, coopInfo]
  );

  const setProductProperty = useCallback(
    async (id?: number, propertyName?: string, value?: any) => {
      if (
        !cart ||
        !Array.isArray(cart.products) ||
        isNaN(id as number) ||
        !propertyName
      ) {
        return;
      }

      const tmpCart = { ...cart };

      const cartItemIndex = tmpCart?.products?.findIndex(
        (i) => i.productId === id
      );

      if (cartItemIndex === undefined || cartItemIndex === -1) {
        return;
      }
      (tmpCart.products[cartItemIndex] as { [key: string]: any })[
        propertyName
      ] = value;

      console.log("Set Product Property: ", id, propertyName, value);

      if (
        propertyName === "quantity" ||
        propertyName === "shippingQuantity" ||
        propertyName === "price"
      ) {
        const recalculated = await CartHelper.recalculateCoop(
          tmpCart,
          coopInfo?.available,
          coopInfo?.acbAllowanceEnabled,
          coopInfo?.masterDealerId
        );
        setCart(recalculated);
      } else {
        setCart(tmpCart);
      }
    },
    [cart, setCart, coopInfo]
  );

  const setProductPrice = useCallback(
    (id?: number, value?: number) => {
      if (isNaN(id as number) || isNaN(value as number)) return;

      console.log("🔧 Updating product price:", id, value);
      setProductProperty(id, "price", value); // this already triggers coop recalculation
    },
    [setProductProperty]
  );

  const setQuantity = useCallback(
    (id?: number, value?: number, type?: "in-store" | "shipping") => {
      if (isNaN(value as number)) {
        return;
      }

      const cartItemIndex = cart?.products?.findIndex(
        (i) => i.productId === id
      );

      if (cartItemIndex === undefined || cartItemIndex === -1) {
        return;
      }

      const item = cart?.products?.[cartItemIndex];
      if (!item) {
        return;
      }

      console.log("setQuantity - Set Quantity: ", id, value, type);
      setProductProperty(
        id,
        type === "shipping" ? "shippingQuantity" : "quantity",
        value
      );
    },
    [setProductProperty]
  );

  const setInStoreQuantity = useCallback(
    (id?: number, value?: number) => {
      if (isNaN(value as number)) {
        return;
      }

      const cartItemIndex = cart?.products?.findIndex(
        (i) => i.productId === id
      );

      if (cartItemIndex === undefined || cartItemIndex === -1) {
        return;
      }

      const item = cart?.products?.[cartItemIndex];
      if (!item) {
        return;
      }

      const shippingQuantity = item.shippingQuantity || 0;
      const totalQuantity = (value || 0) + shippingQuantity;

      setProductProperty(id, "quantity", totalQuantity);
    },
    [setProductProperty]
  );

  const setCoopApplied = useCallback(
    (id?: number, value?: number) => {
      if (isNaN(value as number)) {
        return;
      }

      const cartItemIndex = cart?.products.findIndex((i) => i.productId === id);

      if (
        cartItemIndex === undefined ||
        cartItemIndex === -1 ||
        (cart?.products?.[cartItemIndex]?.coop || 0) < (value || 0)
      ) {
        return;
      }

      setProductProperty(id, "coopApplied", value);
    },
    [cart, setProductProperty]
  );

  const setShippingFee = useCallback(
    (value?: number) => {
      set("shippingFee", value);
    },
    [set]
  );

  const setDiscountTypeWithCartType = useCallback((props:Cart["discountConfig"])=>{
    if (!props||isNaN(props?.value)) return;
   set("discountConfig",props)
  },[setProductProperty])

  const setDiscount = useCallback(
    (value?: number, isOffSite?: boolean) => {
      if (isNaN(value as number)) return;

      const currentTotal = CartHelper.getTotalAmount(cart);
      if (
        currentTotal +
          (isOffSite ? cart?.offSiteDiscount || 0 : cart?.discount || 0) -
          (value || 0) <
        0
      )
        return;
      if (isOffSite) set("offSiteDiscount", value);
      else set("discount", value);
    },
    [set, cart]
  );

  const setSelectedToShip = useCallback(
    (id?: number, value?: boolean) => {
      if (!isBoolean(value)) {
        return;
      }

      setProductProperty(id, "selectedToShip", value);
    },
    [setProductProperty]
  );

  const setShippingQuantity = useCallback(
    (id?: number, value?: number) => {
      if (isNaN(value as number)) {
        return;
      }

      const cartItemIndex = cart?.products?.findIndex(
        (i) => i.productId === id
      );

      if (cartItemIndex === undefined || cartItemIndex === -1) {
        return;
      }

      const item = cart?.products?.[cartItemIndex];
      if (!item) {
        return;
      }

      const inStoreQuantity =
        (item.quantity || 0) - (item.shippingQuantity || 0);
      const totalQuantity = inStoreQuantity + (value || 0);

      setProductProperty(id, "quantity", totalQuantity);
      setProductProperty(id, "shippingQuantity", value);
    },
    [setProductProperty]
  );

  const setShippingData = useCallback(
    (value?: ShippingData) => {
      set("shippingData", value);
    },
    [set]
  );

  const setCard = useCallback(
    (cardNumber?: string, expiration?: string) => {
      const tmpCart = cloneDeep(cart) as Cart;
      tmpCart.card = cardNumber;
      tmpCart.cardExpired = expiration;
      setCart(tmpCart);
    },
    [cart, setCart]
  );

  const setPaymentMethod = useCallback(
    (paymentMethodId?: string, cardBrand?: string, cardLast4?: string) => {
      const tmpCart = cloneDeep(cart) as Cart;
      tmpCart.paymentMethodId = paymentMethodId;
      tmpCart.cardBrand = cardBrand;
      tmpCart.cardLast4 = cardLast4;
      setCart(tmpCart);
    },
    [cart, setCart]
  );

  const clear = useCallback(() => {
    setCart(undefined);
  }, [setCart]);

  const setOrderId = useCallback(
    (value?: number) => {
      set("orderId", value);
    },
    [set]
  );

  const setCouponAmount = useCallback(
    (value?: number) => {
      set("couponAmount", value);
    },
    [set]
  );

  const setCouponQuantity = useCallback(
    (value?: number) => {
      if (isNaN(value as number)) return;

      set("couponQuantity", value);
    },
    [set]
  );

  const setDiscountComment = useCallback(
    (comment?: string) => {
      console.log("setDiscountComment - Comment: ", comment);
      set("discountComment", comment);
    },
    [set]
  );

  return {
    cart,
    add,
    remove,
    setQuantity,
    setCoopApplied,
    setShippingFee,
    setDiscount,
    setSelectedToShip,
    setShippingQuantity,
    setInStoreQuantity,
    setShippingData,
    setCard,
    setPaymentMethod,
    clear,
    setOrderId,
    setCouponAmount,
    setCouponQuantity,
    setCart: setCartWithCoop,
    setProductPrice,
    setDiscountTypeWithCartType,
    setDiscountComment
  };
};
